import React, { useEffect, useState } from 'react';
import { API_URL } from './constants';
import { getTherapistName, getServicePrice } from './utils';

const ANY_PROVIDER = { Id: 'any', DisplayName: 'Any provider' };

function TherapistStep({ selectedServices, selectedCenter, therapistsByService, loadingTherapists, selectedTherapists, onTherapistChange }) {
  const [expandedSvcId, setExpandedSvcId] = useState(null);

  // Pre-select "Any provider" for any service without a selection — keeps the
  // Continue button enabled and matches the spec's default state.
  useEffect(() => {
    if (loadingTherapists) return;
    selectedServices.forEach(svc => {
      if (!selectedTherapists[svc.id]) {
        onTherapistChange(svc.id, ANY_PROVIDER);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTherapists, selectedServices.length]);

  const Spinner = ({ text }) => (
    <div className="loading">
      <div className="spinner"></div>
      <span>{text || 'Loading...'}</span>
    </div>
  );

  function getTherapistImage(therapist) {
    const id = therapist?.Id || therapist?.id;
    if (!id || id === 'any') return null;
    return `${API_URL}/api/therapist-image/${id}?size=px200`;
  }

  // Master badge comes from our ops-app careerLevel tier
  // (Employee.careerLevel in the booking backend's Prisma schema).
  // Waxers: MW1–MW4 → "MASTER". Nail techs: PP (Polish Pro, single top
  // level since 2026-08-07) → "MASTER NAIL TECH" (Lindsay 2026-08-08 —
  // guest-facing label is Master Nail Tech, not Polish Pro). Legacy MN*
  // codes count too. Full-text variants matched in case the format changes.
  function isMasterTier(therapist) {
    const tier = (therapist?.careerLevel || '').toUpperCase();
    if (!tier) return false;
    return tier.startsWith('MW') || tier.includes('MASTER WAXER')
      || tier === 'PP' || tier.startsWith('PP') || tier.includes('POLISH PRO')
      || tier.startsWith('MN') || tier.includes('MASTER NAIL');
  }

  function masterLabel(therapist) {
    const tier = (therapist?.careerLevel || '').toUpperCase();
    if (tier.startsWith('PP') || tier.includes('POLISH PRO') || tier.startsWith('MN') || tier.includes('MASTER NAIL')) {
      return 'MASTER NAIL TECH';
    }
    return 'MASTER';
  }

  function getInitial(therapist) {
    const name = getTherapistName(therapist) || '';
    return (name.trim()[0] || '?').toUpperCase();
  }

  function getDefaultPriceForService(serviceId) {
    const s = selectedServices.find(x => x.id === serviceId);
    if (!s) return 0;
    const providers = therapistsByService[serviceId] || [];
    const basePrice = getServicePrice(s);
    if (providers.length === 0) return basePrice;
    const prices = providers.map(t => {
      const cp = (t.Price !== null && t.Price !== undefined) ? t.Price : null;
      return cp !== null ? cp : basePrice;
    });
    return Math.min(...prices);
  }

  function formatDiff(n) {
    return Number.isInteger(n) ? `+$${n}` : `+$${n.toFixed(2)}`;
  }

  function sortMasterFirst(list) {
    return [...list].sort((a, b) => (isMasterTier(a) ? 0 : 1) - (isMasterTier(b) ? 0 : 1));
  }

  function isAnySelection(selection) {
    return !selection || (selection.Id || selection.id) === 'any';
  }

  // Cascade: when picking Provider X for Service A AND service A was on "Any",
  // auto-apply the same provider to OTHER services that (a) are still on
  // "Any" and (b) have that provider available.
  function handleProviderPick(svcId, provider) {
    const previousValue = selectedTherapists[svcId];
    const wasAny = isAnySelection(previousValue);
    const providerId = provider.Id || provider.id;

    onTherapistChange(svcId, provider);
    setExpandedSvcId(null);

    if (!wasAny || providerId === 'any') return;

    selectedServices.forEach(otherSvc => {
      if (otherSvc.id === svcId) return;
      if (!isAnySelection(selectedTherapists[otherSvc.id])) return;
      const matched = (therapistsByService[otherSvc.id] || []).find(
        t => (t.Id || t.id) === providerId
      );
      if (matched) onTherapistChange(otherSvc.id, matched);
    });
  }

  function handleAnyPick(svcId) {
    onTherapistChange(svcId, ANY_PROVIDER);
    setExpandedSvcId(null);
  }

  function toggleExpand(svcId) {
    setExpandedSvcId(prev => (prev === svcId ? null : svcId));
  }

  if (loadingTherapists) {
    return (
      <div className="step-content">
        <h1 className="category-page-title">Who's your estie bestie?</h1>
        {selectedCenter?.name && (
          <p className="category-page-subtitle">at {selectedCenter.name}</p>
        )}
        <Spinner text="Loading available providers..." />
      </div>
    );
  }

  if (selectedServices.length === 0) {
    return (
      <div className="step-content">
        <h1 className="category-page-title">Who's your estie bestie?</h1>
        <p>No services selected.</p>
      </div>
    );
  }

  function renderFace(therapist, opts = {}) {
    const id = therapist.Id || therapist.id;
    const imgUrl = getTherapistImage(therapist);
    const master = opts.master ?? isMasterTier(therapist);
    return (
      <span
        key={`face-${id}`}
        className={`provider-face${master ? ' provider-face--master' : ''}`}
        aria-hidden="true"
      >
        {master && (
          <span className="therapist-master-ribbon therapist-master-ribbon--mini">★</span>
        )}
        <span className="provider-face-fallback">{getInitial(therapist)}</span>
        {imgUrl && (
          <img src={imgUrl} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
        )}
      </span>
    );
  }

  return (
    <div className="step-content">
      <h1 className="category-page-title">Who's your estie bestie?</h1>
      {selectedCenter?.name && (
        <p className="category-page-subtitle">at {selectedCenter.name}</p>
      )}

      <div className="provider-stack">
        {selectedServices.map(svc => {
          const therapists = therapistsByService[svc.id] || [];
          const sorted = sortMasterFirst(therapists);
          const selected = selectedTherapists[svc.id];
          const selectedId = selected ? (selected.Id || selected.id || null) : 'any';
          const isAny = isAnySelection(selected);
          const isExpanded = expandedSvcId === svc.id;

          const pickedProvider = !isAny
            ? therapists.find(t => (t.Id || t.id) === selectedId) || selected
            : null;
          const pickedIsMaster = pickedProvider && isMasterTier(pickedProvider);
          const pickedName = pickedProvider ? getTherapistName(pickedProvider) : 'Any provider';

          // Top 3 face avatars + +N pill if more
          const faceCount = Math.min(3, sorted.length);
          const topFaces = sorted.slice(0, faceCount);
          const remaining = Math.max(0, sorted.length - faceCount);

          return (
            <section
              key={svc.id}
              className={`provider-row ${isExpanded ? 'expanded' : ''}`}
              aria-expanded={isExpanded}
            >
              {/* Collapsed summary — also serves as expand toggle */}
              <button
                type="button"
                className="provider-row-summary-btn"
                onClick={() => toggleExpand(svc.id)}
                aria-label={`${svc.display_name || svc.name} — ${pickedName}, tap to ${isExpanded ? 'collapse' : 'change'}`}
              >
                <div className="provider-row-top">
                  <span className="provider-row-service">
                    {(svc.display_name || svc.name || '').toUpperCase()}
                  </span>
                  <span className="provider-row-toggle">
                    {isExpanded
                      ? 'Collapse ▴'
                      : isAny
                        ? 'OR pick your estie ▾'
                        : 'Change ▾'}
                  </span>
                </div>

                <div className="provider-row-bottom">
                  <span className="provider-row-summary">
                    {pickedProvider ? (
                      renderFace(pickedProvider, { master: pickedIsMaster })
                    ) : (
                      <span className="provider-face provider-face--any" aria-hidden="true">✓</span>
                    )}
                    <span className="provider-row-name">{pickedName}</span>
                  </span>

                  {!isExpanded && (
                    <span className="provider-face-stack" aria-hidden="true">
                      {topFaces.map(t => renderFace(t))}
                      {remaining > 0 && (
                        <span className="provider-face provider-face--more">+{remaining}</span>
                      )}
                    </span>
                  )}
                </div>
              </button>

              {/* Expanded grid */}
              {isExpanded && (
                therapists.length === 0 ? (
                  <p className="provider-row-empty">
                    No providers available for {svc.display_name || svc.name} at this location.
                  </p>
                ) : (
                  <div className="therapist-mini-grid provider-row-grid">
                    {/* "Any provider" pseudo-card */}
                    <button
                      type="button"
                      className={`therapist-mini-card ${isAny ? 'selected' : ''}`}
                      onClick={() => handleAnyPick(svc.id)}
                      aria-pressed={isAny}
                    >
                      <span className="therapist-mini-avatar therapist-mini-avatar--any" aria-hidden="true">
                        ✓
                      </span>
                      <span className="therapist-mini-name therapist-mini-name--any">Any provider</span>
                    </button>

                    {therapists.map(therapist => {
                      const id = therapist.Id || therapist.id;
                      const name = getTherapistName(therapist);
                      const imgUrl = getTherapistImage(therapist);
                      const selectedHere = selectedId === id;
                      const master = isMasterTier(therapist);
                      const customPrice = (therapist.Price !== null && therapist.Price !== undefined) ? therapist.Price : null;
                      const basePrice = getServicePrice(svc);
                      const providerPrice = customPrice !== null ? customPrice : basePrice;
                      const defaultPrice = getDefaultPriceForService(svc.id);
                      const premiumDiff = providerPrice - defaultPrice;

                      return (
                        <button
                          type="button"
                          key={id}
                          className={`therapist-mini-card ${selectedHere ? 'selected' : ''}`}
                          onClick={() => handleProviderPick(svc.id, therapist)}
                          aria-pressed={selectedHere}
                        >
                          {master && <span className={`therapist-master-ribbon ${masterLabel(therapist) !== 'MASTER' ? 'therapist-master-ribbon--long' : ''}`} aria-label={masterLabel(therapist)}>{masterLabel(therapist)}</span>}
                          <span className="therapist-mini-avatar" aria-hidden="true">
                            <span className="therapist-mini-avatar-fallback">{getInitial(therapist)}</span>
                            {imgUrl && (
                              <img
                                src={imgUrl}
                                alt=""
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                          </span>
                          <span className="therapist-mini-name">{name}</span>
                          {premiumDiff > 0 && (
                            <span className="therapist-mini-price-diff">{formatDiff(premiumDiff)}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

export default TherapistStep;
